import { useState } from 'react';
import {
  History,
  Filter,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  MinusCircle,
  TrendingDown,
  Calendar,
  DollarSign,
  FileText,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSupplierBids } from '@/hooks/use-sourcing';
import {
  type BidStatus,
  BID_STATUS_LABELS,
  BID_STATUS_COLORS,
} from '@/types/sourcing';
import { toast } from 'sonner';

type HistoryFilter = 'all' | 'accepted' | 'rejected' | 'withdrawn' | 'outbid';

const HISTORY_STATUSES: BidStatus[] = ['accepted', 'rejected', 'withdrawn', 'outbid'];

function getOutcomeIcon(status: BidStatus) {
  switch (status) {
    case 'accepted':
      return CheckCircle;
    case 'rejected':
      return XCircle;
    case 'withdrawn':
      return MinusCircle;
    case 'outbid':
      return TrendingDown;
    default:
      return FileText;
  }
}

function getOutcomeColor(status: BidStatus): string {
  switch (status) {
    case 'accepted':
      return 'text-green-600';
    case 'rejected':
      return 'text-red-600';
    case 'withdrawn':
      return 'text-gray-600';
    case 'outbid':
      return 'text-orange-600';
    default:
      return 'text-muted-foreground';
  }
}

export function BidHistoryPage() {
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const {
    data, isLoading, error, refetch 
  } = useSupplierBids();

  // Filter to only historical (completed) bids
  const allHistoricalBids = (data?.data ?? []).filter((bid) =>
    HISTORY_STATUSES.includes(bid.status)
  );

  const filteredBids = filter === 'all'
    ? allHistoricalBids
    : allHistoricalBids.filter((b) => b.status === filter);

  // Calculate summary stats
  const stats = {
    total: allHistoricalBids.length,
    accepted: allHistoricalBids.filter((b) => b.status === 'accepted').length,
    rejected: allHistoricalBids.filter((b) => b.status === 'rejected').length,
    withdrawn: allHistoricalBids.filter((b) => b.status === 'withdrawn').length,
    outbid: allHistoricalBids.filter((b) => b.status === 'outbid').length,
    totalValue: allHistoricalBids
      .filter((b) => b.status === 'accepted')
      .reduce((sum, b) => sum + b.total, 0),
  };

  const winRate = stats.total > 0
    ? Math.round((stats.accepted / stats.total) * 100)
    : 0;

  const handleExport = () => {
    if (filteredBids.length === 0) {
      toast.info('No data to export');
      return;
    }
    const exportData = filteredBids.map((b) => ({
      bidId: b.bidId,
      eventId: b.eventId,
      status: b.status,
      total: b.total,
      currency: b.currency,
      submittedAt: b.submittedAt ?? '',
      updatedAt: b.updatedAt,
    }));
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map((row) =>
        headers.map((h) => {
          const val = row[h as keyof typeof row];
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replaceAll('"', '""')}"`;
          }
          return String(val ?? '');
        }).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bid-history.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredBids.length} historical bids`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="h-24 animate-pulse bg-muted" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-24 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load bid history</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-blue-100 p-3">
              <History className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Completed</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Won</p>
              <p className="text-xl font-bold text-green-600">{stats.accepted}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-purple-100 p-3">
              <TrendingDown className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Win Rate</p>
              <p className="text-xl font-bold">{winRate}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-amber-100 p-3">
              <DollarSign className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Won Value</p>
              <p className="text-xl font-bold">${stats.totalValue.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </Button>
        <Button
          variant={filter === 'accepted' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('accepted')}
          className={filter === 'accepted' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Won ({stats.accepted})
        </Button>
        <Button
          variant={filter === 'rejected' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('rejected')}
          className={filter === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          <XCircle className="h-4 w-4 mr-1" />
          Lost ({stats.rejected})
        </Button>
        <Button
          variant={filter === 'outbid' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('outbid')}
          className={filter === 'outbid' ? 'bg-orange-600 hover:bg-orange-700' : ''}
        >
          <TrendingDown className="h-4 w-4 mr-1" />
          Outbid ({stats.outbid})
        </Button>
        <Button
          variant={filter === 'withdrawn' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('withdrawn')}
        >
          <MinusCircle className="h-4 w-4 mr-1" />
          Withdrawn ({stats.withdrawn})
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Bid History List */}
      <HistoryContent bids={filteredBids} filter={filter} />
    </div>
  );
}

interface HistoryContentProps {
  readonly bids: Array<{
    bidId: string;
    eventId: string;
    status: BidStatus;
    total: number;
    currency: string;
    lines: Array<{ lineId: string }>;
    submittedAt?: string;
    updatedAt: string;
    notes?: string;
  }>;
  readonly filter: HistoryFilter;
}

function HistoryContent({
  bids, filter 
}: HistoryContentProps) {
  if (bids.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <History className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium">No bid history</p>
          <p className="text-muted-foreground">
            {filter === 'all'
              ? 'You have no completed bids yet'
              : `No bids with outcome "${BID_STATUS_LABELS[filter]}"`}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Bid History</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {bids.map((bid) => {
            const OutcomeIcon = getOutcomeIcon(bid.status);
            const outcomeColor = getOutcomeColor(bid.status);

            return (
              <div
                key={bid.bidId}
                className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`rounded-full p-2 bg-muted ${outcomeColor}`}>
                    <OutcomeIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Event {bid.eventId}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${BID_STATUS_COLORS[bid.status]}`}>
                        {BID_STATUS_LABELS[bid.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {bid.submittedAt
                          ? new Date(bid.submittedAt).toLocaleDateString()
                          : new Date(bid.updatedAt).toLocaleDateString()}
                      </span>
                      <span>{bid.lines.length} line items</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    ${bid.total.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">{bid.currency}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Bid History</h1>
      <p className="text-muted-foreground">
        Review your past bids and track your win rate
      </p>
    </div>
  );
}
